import {
  Directive,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  type SimpleChanges,
} from '@angular/core';
import { ButtonColor, ButtonType } from './types';

/**
 * Transforms regular button elements into styled components with consistent theming and behavior.
 *
 * @remarks
 * Applied to button elements using the `bbButton` selector, this directive adds Bootstrap-based
 * CSS classes for styling and manages dynamic updates to color and size properties. Supports
 * various color schemes, sizes, and special modes like circular buttons and block-level display.
 *
 * ### Color Background Mode
 * For buttons on darker backgrounds, use the `On Color` palette by adding `.btn-on-color` class
 * to a parent container. This affects all descendant buttons and supports `primary`, `secondary`,
 * and `link` color variants.
 *
 * ### Accessibility
 * The directive itself doesn't provide accessibility properties. Add `aria-*` attributes
 * directly to the button element as needed for screen reader compatibility.
 *
 * @example
 *
 * ```html
 * <button bbButton color="primary" buttonSize="md">
 *   Primary Action
 * </button>
 *
 * <button bbButton color="tertiary" [circle]="true">
 *   <bb-icon-ui name="add" size="sm"></bb-icon-ui>
 * </button>
 * ```
 */
@Directive({
  selector: 'button[bbButton]',
  standalone: false,
})
export class ButtonDirective implements OnInit, OnChanges {
  /**
   * Button's type atrribute with default value.
   */
  @HostBinding('type')
  @Input()
  type: ButtonType = 'button';

  /**
   * The color for the button. Defaults to 'primary'.
   * If a custom string used, then a css class is going to be added. The pattern is as follows:
   * `btn-customColor`
   *
   * @example
   *
   * ```html
   * <button bbButton color="secondary">Secondary Action</button>
   * <button bbButton color="tertiary">Tertiary Action</button>
   * <button bbButton color="dark">Dark Theme</button>
   * ```
   */
  @Input() color: ButtonColor | string = 'primary';

  /**
   * Size of the button.
   *
   * @example
   *
   * ```html
   * <button bbButton buttonSize="sm" color="primary">Small Button</button>
   * <button bbButton buttonSize="md" color="secondary">Medium Button</button>
   * ```
   */
  @Input() buttonSize: 'sm' | 'md' = 'md';

  /**
   * The flag to indicate whether the button should fill the container that it is in. Defaults to 'false'.
   */
  @HostBinding('class.btn-block')
  @Input()
  block = false;
  /**
   * The flag to indicate whether the button should be in a circular shape. Defaults to 'false'.
   * This will only work when there's only an icon inside the button without a text.
   */
  @HostBinding('class.btn-circle')
  @Input()
  circle = false;

  constructor(
    private readonly renderer: Renderer2,
    private readonly hostElement: ElementRef,
  ) {}

  /**
   * Applies initial CSS classes to transform the button element.
   *
   * @remarks
   * Adds Bootstrap base classes and styling based on current color and size properties.
   * Executes automatically when the directive is initialized by Angular framework.
   */
  ngOnInit(): void {
    this.renderer.addClass(this.hostElement.nativeElement, 'btn');
    this.renderer.addClass(this.hostElement.nativeElement, `btn-${this.color}`);
    this.renderer.addClass(this.hostElement.nativeElement, `btn-${this.buttonSize}`);
  }

  /**
   * Updates CSS classes when input properties change.
   *
   * @remarks
   * Handles dynamic changes to color and buttonSize properties by removing old CSS classes
   * and applying new ones. Ensures button styling stays synchronized with component state
   * without requiring full re-initialization.
   *
   * @param changes - Object containing previous and current values for changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.color) {
      if (changes.color.previousValue) {
        this.renderer.removeClass(this.hostElement.nativeElement, `btn-${changes.color.previousValue}`);
      }

      if (changes.color.currentValue) {
        this.renderer.addClass(this.hostElement.nativeElement, `btn-${changes.color.currentValue}`);
      }
    }

    if (changes.buttonSize) {
      if (changes.buttonSize.previousValue) {
        this.renderer.removeClass(this.hostElement.nativeElement, `btn-${changes.buttonSize.previousValue}`);
      }

      if (changes.buttonSize.currentValue) {
        this.renderer.addClass(this.hostElement.nativeElement, `btn-${changes.buttonSize.currentValue}`);
      }
    }
  }
}
